/**
 * A condition as editable rows, and back.
 *
 * A form can edit a list; it cannot edit a tree. So a posting rule's `match` is
 * edited as rows — one comparison each, all of which must hold — and the tree
 * the engine evaluates is built from them on save:
 *
 *   [{ field: 'scheme', operator: 'equals', value: 'reverseCharge', valueType: 'string' },
 *    { field: 'rate',   operator: 'in',     value: '0.25, 0.2',     valueType: 'number' }]
 *   → { all: [{ field: 'scheme', operator: 'equals', value: 'reverseCharge' },
 *             { field: 'rate',   operator: 'in',     value: [0.25, 0.2] }] }
 *
 * Values are typed text in a form, so each row carries the type of the field it
 * tests. Comparison is strict: `selfAccounted` must be the boolean true, not the
 * string "true", and an official code like ES "09" must stay a string rather
 * than become the number 9 — which is why the type comes from the field rather
 * than being guessed from how the text looks.
 */

const LIST_OPERATORS = new Set(['in', 'notIn'])

const coerce = (text, valueType) => {
    const raw = typeof text === 'string' ? text.trim() : text
    if (valueType === 'boolean') {
        if (raw === true || raw === 'true') return true
        if (raw === false || raw === 'false') return false
        return raw
    }
    if (valueType === 'number') {
        const n = typeof raw === 'number' ? raw : Number(raw)
        return raw === '' || Number.isNaN(n) ? raw : n
    }
    return raw
}

/**
 * Rows → condition. Incomplete rows (no field or no operator) are skipped, so a
 * half-added row does not become a condition nobody meant. No usable rows means
 * no condition — `undefined` — which is how a catch-all rule is written.
 */
export const rowsToCondition = (rows) => {
    if (!Array.isArray(rows)) return undefined
    const leaves = rows
        .filter(r => r && r.field && r.operator)
        .map(({ field, operator, value, valueType }) => ({
            field,
            operator,
            value: LIST_OPERATORS.has(operator)
                ? String(value ?? '').split(',').map(v => v.trim()).filter(Boolean).map(v => coerce(v, valueType))
                : coerce(value ?? '', valueType)
        }))
    return leaves.length ? { all: leaves } : undefined
}

/**
 * Condition → rows, for the shapes rows can express: a single comparison, an
 * `all` of comparisons, or a bare array of them. Anything else — `any`,
 * nesting, quantifiers, a comparison against another field — returns null, and
 * the caller must leave that condition as it is rather than flatten it into
 * something that means less.
 */
export const conditionToRows = (condition) => {
    if (!condition || typeof condition !== 'object') return []
    const leaves = Array.isArray(condition) ? condition
        : Array.isArray(condition.all) ? condition.all
        : condition.field !== undefined ? [condition]
        : null
    if (!leaves) return null

    const rows = []
    for (const leaf of leaves) {
        if (!leaf || typeof leaf !== 'object' || leaf.field === undefined) return null
        if (leaf.valueFrom !== undefined || leaf.all || leaf.any || leaf.some || leaf.none) return null
        const operator = leaf.operator ?? 'equals'
        const values = Array.isArray(leaf.value) ? leaf.value : [leaf.value]
        const sample = values.find(v => v !== undefined && v !== null)
        rows.push({
            field: leaf.field,
            operator,
            value: values.map(v => (v === undefined || v === null ? '' : String(v))).join(', '),
            valueType: typeof sample === 'boolean' ? 'boolean' : typeof sample === 'number' ? 'number' : 'string'
        })
    }
    return rows
}
